/**
 * Utilitaire pour gérer les permissions utilisateur
 */

/**
 * Vérifie si l'utilisateur est superadmin (Administrateur système)
 * @returns {boolean} true si l'utilisateur est superadmin
 */
export const isSuperAdmin = () => {
  try {
    const userData = localStorage.getItem('user');
    if (!userData) return false;
    
    const user = JSON.parse(userData);
    if (user.roles && Array.isArray(user.roles)) {
      return user.roles.some(role => {
        // Gérer les différents formats de rôle (objet avec .name ou string)
        const roleName = typeof role === 'object' && role !== null ? role.name : role;
        return roleName === 'Administrateur système' || 
               (typeof roleName === 'string' && roleName.toLowerCase().includes('administrateur système'));
      });
    }
    return false;
  } catch (error) {
    console.error('Error checking superadmin:', error);
    return false;
  }
};

/**
 * Récupère les permissions de l'utilisateur depuis le localStorage
 * @returns {Array<string>} Liste des noms de permissions
 */
export const getUserPermissions = () => {
  try {
    const userData = localStorage.getItem('user');
    if (!userData) return [];
    
    const user = JSON.parse(userData);
    return user.permissions || [];
  } catch (error) {
    console.error('Error getting user permissions:', error);
    return [];
  }
};

/**
 * Vérifie si l'utilisateur a une permission spécifique
 * @param {string} permission - Nom de la permission à vérifier
 * @returns {boolean} true si l'utilisateur a la permission
 */
export const hasPermission = (permission) => {
  // Le superadmin a tous les droits
  if (isSuperAdmin()) return true;
  
  if (!permission) return true; // Si pas de permission requise, autoriser
  
  const userPermissions = getUserPermissions();
  
  // Vérifier la permission exacte
  if (userPermissions.includes(permission)) {
    return true;
  }
  
  // Vérifier les formats alternatifs (avec tiret ou point)
  const altFormats = [
    permission.replace('.', ' - '),
    permission.replace(' - ', '.'),
    permission.replace('-', '.'),
  ];
  
  return altFormats.some(alt => userPermissions.includes(alt));
};

/**
 * Vérifie si l'utilisateur a au moins une des permissions spécifiées
 * @param {Array<string>} permissions - Liste des permissions à vérifier
 * @returns {boolean} true si l'utilisateur a au moins une permission
 */
export const hasAnyPermission = (permissions) => {
  if (!permissions || permissions.length === 0) return true;
  return permissions.some(permission => hasPermission(permission));
};

/**
 * Vérifie si l'utilisateur a toutes les permissions spécifiées
 * @param {Array<string>} permissions - Liste des permissions à vérifier
 * @returns {boolean} true si l'utilisateur a toutes les permissions
 */
export const hasAllPermissions = (permissions) => {
  if (!permissions || permissions.length === 0) return true;
  return permissions.every(permission => hasPermission(permission));
};

/**
 * Mapping des routes vers les permissions requises
 */
export const routePermissions = {
  '/hse-dashboard': [
    'dashboard.view',
    'dashboard.hse',
    'dashboard.management',
    'Tableau de bord - Voir',
    'Tableau de bord - HSE',
    'Tableau de bord - Management',
  ],
  '/hse-predictive-ai': ['predictive-ai.view', 'IA prédictive - Voir'],
  '/dashboard-management': [
    'dashboard.view',
    'dashboard.hse',
    'dashboard.management',
    'Tableau de bord - Voir',
    'Tableau de bord - HSE',
    'Tableau de bord - Management',
  ],
  '/observations': ['observations.view', 'Observations - Voir'],
  '/workflow/observations': ['workflow.view', 'Workflow - Voir'],
  '/workflow/history': ['workflow.history', 'Workflow - Historique'],
  '/users/list': ['users.view', 'Utilisateurs - Voir'],
  '/users/roles': ['roles.view', 'permissions.view', 'Rôles - Voir', 'Permissions - Voir'],
  '/users/sites': ['site-assignment.view', 'Attribution Sites - Voir'],
  '/users/audit': ['activity-logs.view', 'Activity Logs - View'],
  '/sites': ['sites.view', 'Sites - Voir'],
  '/notifications': ['notifications.view'],
  '/alerts': ['notifications.view'],
  '/user-profile-notification': ['notifications.view'],
  '/analytics-hse': ['analytics.view', 'analytics.hse'],
  '/reports/monthly': ['reports.view', 'reports.monthly'],
  '/reports/sites': ['reports.view', 'reports.sites'],
  '/reports/custom': ['reports.view', 'reports.custom'],
  '/risk-mapping': ['risk-mapping.view'],
  '/gamification/leaderboard': ['gamification.view', 'gamification.leaderboard'],
  '/gamification/badges': ['gamification.view', 'gamification.badges'],
  '/gamification/points': ['gamification.view', 'gamification.points'],
  '/gamification/sites': ['gamification.view', 'gamification.leaderboard'],
  '/compliance/audit-trail': ['compliance.view', 'compliance.audit-trail'],
  '/compliance/traceability': ['compliance.view', 'compliance.traceability'],
  '/compliance/audit-reports': ['compliance.view', 'compliance.audit-reports'],
  '/compliance/iso': ['compliance.view', 'compliance.iso'],
  '/card-of-month': ['card-of-month.view', 'Carte du mois - Voir'],
  '/card-of-month/scoring-config': ['Configuration scoring - Voir', 'Configuration scoring - Modifier', 'Carte du mois - Sélectionner'],
};

/**
 * Vérifie si l'utilisateur peut accéder à une route
 * @param {string} route - Route à vérifier
 * @returns {boolean} true si l'utilisateur peut accéder
 */
export const canAccessRoute = (route) => {
  // Le superadmin a accès à toutes les routes
  if (isSuperAdmin()) return true;
  
  const requiredPermissions = routePermissions[route];
  if (!requiredPermissions) return true; // Si pas de permission définie, autoriser par défaut
  
  return hasAnyPermission(requiredPermissions);
};

